import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetchJson } from "@/lib/authenticatedFetch";
import type {
  CreateCheckoutSessionResponse,
  CreateSetupIntentResponse,
  DetachPaymentMethodResponse,
  DowngradeStripeSubscriptionResponse,
  PreviewUpgradeResponse,
  ReactivateSubscriptionResponse,
  StripeBillingDetailsResponse,
  UpgradeStripeSubscriptionResponse,
} from "@/types/billing";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { cuKeys } from "./useCU";
import { planOptionsKeys } from "./usePlanOptions";
import { subscriptionKeys } from "./useSubscription";

function refetchAllBillingQueries(queryClient: QueryClient) {
  queryClient.refetchQueries({ queryKey: subscriptionKeys.all });
  queryClient.refetchQueries({ queryKey: cuKeys.all });
  queryClient.refetchQueries({ queryKey: planOptionsKeys.all });
  queryClient.refetchQueries({ queryKey: stripeBillingDetailsKeys.all });
  queryClient.refetchQueries({ queryKey: previewUpgradeKeys.all });
}

export function invalidateAllBillingQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: subscriptionKeys.all });
  queryClient.invalidateQueries({ queryKey: cuKeys.all });
  queryClient.invalidateQueries({ queryKey: planOptionsKeys.all });
  queryClient.invalidateQueries({ queryKey: stripeBillingDetailsKeys.all });
  queryClient.invalidateQueries({ queryKey: previewUpgradeKeys.all });
}

export const stripeBillingDetailsKeys = {
  all: ["stripe-billing-details"] as const,
  detail: (orgId: string) => [...stripeBillingDetailsKeys.all, orgId] as const,
};

export const previewUpgradeKeys = {
  all: ["preview-upgrade"] as const,
  byOrg: (orgId: string) => [...previewUpgradeKeys.all, orgId] as const,
};

export async function fetchStripeBillingDetails(orgId: string) {
  const res = await authenticatedFetchJson<StripeBillingDetailsResponse>(
    `${V2_ENDPOINT}/v1/organizations/${orgId}/stripe/billing-details`,
    { orgId },
  );
  return res.data;
}

export function useStripeBillingDetails(orgId?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: stripeBillingDetailsKeys.detail(orgId || ""),
    queryFn: () => fetchStripeBillingDetails(orgId!),
    enabled: !!orgId && (options?.enabled ?? true),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/**
 * Create a Stripe Checkout Session (free → paid).
 * Returns a clientSecret for embedded checkout in modal.
 */
export function useCreateCheckoutSession(orgId: string) {
  return useMutation({
    mutationFn: async ({ tier, interval }: { tier: string; interval?: string }) => {
      const res = await authenticatedFetchJson<CreateCheckoutSessionResponse>(
        `${V2_ENDPOINT}/v1/organizations/${orgId}/stripe/checkout-sessions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tier, interval }),
          orgId,
        },
      );
      return res.data;
    },
    // No query refetch here — checkout session was just created,
    // payment hasn't completed yet. Polling handles post-payment refresh.
  });
}

/**
 * Upgrade an existing subscription to a higher tier.
 * Backend handles the Stripe subscription update + prorated charge.
 */
export function useUpgradeSubscription(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ newTier, interval }: { newTier: string; interval?: string }) => {
      const res = await authenticatedFetchJson<UpgradeStripeSubscriptionResponse>(
        `${V2_ENDPOINT}/v1/organizations/${orgId}/stripe/subscriptions/upgrade`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ newTier, interval }),
          orgId,
        },
      );
      return res.data;
    },
    // Do not refetch subscription/CU here — webhook hasn't deposited credits yet.
    // Callers should poll or listen for the balance-change event before refetching.
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: stripeBillingDetailsKeys.all });
    },
  });
}

/**
 * Downgrade subscription to a lower tier.
 * Takes effect at end of current billing period.
 */
export function useDowngradeSubscription(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ newTier, interval }: { newTier: string; interval?: string }) => {
      const res = await authenticatedFetchJson<DowngradeStripeSubscriptionResponse>(
        `${V2_ENDPOINT}/v1/organizations/${orgId}/stripe/subscriptions/downgrade`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ newTier, interval }),
          orgId,
        },
      );
      return res.data;
    },
    onSuccess: () => refetchAllBillingQueries(queryClient),
  });
}

/**
 * Reactivate a subscription with a pending cancellation.
 * Clears cancel_at_period_end on Stripe + local cancel_at.
 */
export function useReactivateSubscription(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await authenticatedFetchJson<ReactivateSubscriptionResponse>(
        `${V2_ENDPOINT}/v1/organizations/${orgId}/stripe/subscriptions/reactivate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          orgId,
        },
      );
      return res.data;
    },
    onSuccess: () => refetchAllBillingQueries(queryClient),
  });
}

/**
 * Create a Stripe SetupIntent for collecting a new payment method
 * via Stripe Elements. Returns a clientSecret for the PaymentElement.
 */
export function useCreateSetupIntent(orgId: string) {
  return useMutation({
    mutationFn: async () => {
      const res = await authenticatedFetchJson<CreateSetupIntentResponse>(
        `${V2_ENDPOINT}/v1/organizations/${orgId}/stripe/setup-intents`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          orgId,
        },
      );
      return res.data;
    },
    // No refetch here — card hasn't been saved yet. The modal's handleSuccess
    // refetches billing details after stripe.confirmSetup() completes.
  });
}

/**
 * Detach (remove) a payment method from the organization's Stripe customer.
 */
export function useDetachPaymentMethod(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (paymentMethodId: string) => {
      const res = await authenticatedFetchJson<DetachPaymentMethodResponse>(
        `${V2_ENDPOINT}/v1/organizations/${orgId}/stripe/payment-methods/${paymentMethodId}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          orgId,
        },
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: stripeBillingDetailsKeys.all });
    },
  });
}

/**
 * Fetch pro-rated upgrade pricing for all upgrade-eligible tiers.
 * Only returns data when user has an active paid subscription.
 */
export function usePreviewUpgrade(orgId?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: previewUpgradeKeys.byOrg(orgId || ""),
    queryFn: async () => {
      if (!orgId) throw new Error("No orgId");
      const res = await authenticatedFetchJson<PreviewUpgradeResponse>(
        `${V2_ENDPOINT}/v1/organizations/${orgId}/stripe/subscriptions/preview-upgrade`,
        { method: "GET", orgId },
      );
      return res.data;
    },
    enabled: !!orgId && (options?.enabled ?? true),
    retry: false,
    staleTime: 60_000,
  });
}
